import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { canAccessFinanceRoute } from "@/finance/financeNav";
import { trpc } from "@/lib/trpc";
import { FinanceAccessDeniedState, FinancePageHeader } from "@/components/finance/ui";
import {
  AlertCircle,
  AlertTriangle,
  BarChart3,
  RefreshCw,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  Target,
} from "lucide-react";

const settingsAreas = [
  "Categorias financieras",
  "Umbrales funcionales",
  "Preferencias de vistas",
  "Plantillas de importacion",
  "Reglas de clasificacion",
  "Textos funcionales",
];

function AccessDenied({ title }: { title: string }) {
  return (
    <FinanceAccessDeniedState
      title="Acceso no disponible"
      message={`${title} no esta disponible para tu perfil.`}
    />
  );
}

const euro = new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" });

function formatCents(cents: number | null | undefined) {
  if (cents === null || cents === undefined) return "-";
  return euro.format(cents / 100);
}

function formatPercent(value: number | null | undefined) {
  if (value === null || value === undefined) return "-";
  return `${value.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`;
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString("es-ES");
}

export function FinanceManagementView() {
  const { user } = useAuth();
  const allowed = user ? canAccessFinanceRoute("/accounting/management", user.role) : false;
  const summaryQuery = trpc.finance.dashboard.summary.useQuery(undefined, { enabled: allowed });
  const integrityQuery = trpc.finance.integrity.checks.useQuery(undefined, { enabled: allowed });
  const forecastQuery = trpc.finance.treasury.forecast.useQuery({ horizon: "13w" }, { enabled: allowed });
  const marginQuery = trpc.finance.projectsMargin.list.useQuery(undefined, { enabled: allowed });
  const budgetsQuery = trpc.finance.budgets.list.useQuery(undefined, { enabled: allowed });

  if (!allowed) return <AccessDenied title="Gerencia" />;

  const isLoading = summaryQuery.isLoading || integrityQuery.isLoading || forecastQuery.isLoading || marginQuery.isLoading || budgetsQuery.isLoading;
  const hasError = summaryQuery.isError || integrityQuery.isError || forecastQuery.isError || marginQuery.isError || budgetsQuery.isError;
  const updatedAt = summaryQuery.data?.updatedAt ?? marginQuery.data?.updatedAt ?? budgetsQuery.data?.updatedAt;
  const integrityChecks = integrityQuery.data?.checks ?? [];
  const criticalIntegrity = integrityChecks.filter((check: any) => check.severity === "critical").length;
  const warningIntegrity = integrityChecks.filter((check: any) => check.severity === "warning").length;
  const weekOne = forecastQuery.data?.buckets?.[0];

  const refetchAll = () => {
    summaryQuery.refetch();
    integrityQuery.refetch();
    forecastQuery.refetch();
    marginQuery.refetch();
    budgetsQuery.refetch();
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <FinancePageHeader
          eyebrow="Finanzas y Contabilidad"
          eyebrowIcon={BarChart3}
          title="Gerencia"
          description="Vista directiva reservada a admin sobre KPIs financieros, margen, presupuesto e integridad de datos."
          badgeText={`Admin only · ${formatDateTime(updatedAt)}`}
          primaryAction={{
            label: "Actualizar",
            icon: RefreshCw,
            onClick: refetchAll,
            variant: "outline",
          }}
        />

        <Card className="border-amber-200 bg-amber-50/60">
          <CardContent className="flex flex-col gap-3 px-4 py-4 text-sm text-amber-950 md:flex-row md:items-start">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
            <div className="space-y-1">
              <p className="font-medium">Indicadores derivados de fuentes canonicas</p>
              <p>
                No expone credenciales ni saldos bancarios inventados. Tesoreria mantiene cobertura parcial hasta registrar saldo inicial real.
              </p>
            </div>
          </CardContent>
        </Card>

        {hasError ? (
          <Card>
            <CardContent className="flex flex-col items-start gap-3 px-4 py-6">
              <div className="flex items-center gap-2 text-sm font-medium text-red-700">
                <AlertCircle className="h-4 w-4" />
                Error al cargar Gerencia
              </div>
              <p className="text-sm text-muted-foreground">
                {summaryQuery.error?.message || integrityQuery.error?.message || forecastQuery.error?.message || marginQuery.error?.message || budgetsQuery.error?.message}
              </p>
              <Button variant="outline" size="sm" onClick={refetchAll}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Reintentar
              </Button>
            </CardContent>
          </Card>
        ) : isLoading ? (
          <Card>
            <CardContent className="px-4 py-8 text-sm text-muted-foreground">Cargando panel de gerencia...</CardContent>
          </Card>
        ) : (
          <>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Facturado</p>
                  <p className="mt-1 text-xl font-semibold">{formatCents(marginQuery.data?.totals.invoicedRevenueCents)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Coste real</p>
                  <p className="mt-1 text-xl font-semibold text-rose-700">{formatCents(marginQuery.data?.totals.actualCostCents)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Margen real</p>
                  <p className="mt-1 text-xl font-semibold">{formatCents(marginQuery.data?.totals.actualMarginCents)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Margen</p>
                  <p className="mt-1 text-xl font-semibold">{formatPercent(marginQuery.data?.totals.actualMarginPct)}</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(320px,0.9fr)]">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Plan frente a real</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-md border p-3">
                    <p className="text-xs text-muted-foreground">Presupuesto aprobado</p>
                    <p className="mt-1 text-lg font-semibold">{formatCents(budgetsQuery.data?.totals.approvedCostCents)}</p>
                  </div>
                  <div className="rounded-md border p-3">
                    <p className="text-xs text-muted-foreground">Desviacion presupuesto</p>
                    <p className="mt-1 text-lg font-semibold">{formatCents(budgetsQuery.data?.totals.budgetDeviationCents)}</p>
                  </div>
                  <div className="rounded-md border p-3">
                    <p className="text-xs text-muted-foreground">Uso presupuesto</p>
                    <p className="mt-1 text-lg font-semibold">{formatPercent(budgetsQuery.data?.totals.budgetUsedPct)}</p>
                  </div>
                  <div className="rounded-md border p-3">
                    <p className="text-xs text-muted-foreground">Baselines vigentes</p>
                    <p className="mt-1 text-lg font-semibold">{budgetsQuery.data?.totals.baselineCount ?? 0}</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Integridad ejecutiva</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-3 gap-2">
                    <div className="rounded-md border p-3">
                      <p className="text-xs text-muted-foreground">Criticos</p>
                      <p className="mt-1 text-lg font-semibold text-red-700">{criticalIntegrity}</p>
                    </div>
                    <div className="rounded-md border p-3">
                      <p className="text-xs text-muted-foreground">Avisos</p>
                      <p className="mt-1 text-lg font-semibold text-amber-700">{warningIntegrity}</p>
                    </div>
                    <div className="rounded-md border p-3">
                      <p className="text-xs text-muted-foreground">Checks</p>
                      <p className="mt-1 text-lg font-semibold">{integrityChecks.length}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {integrityChecks.map((check: any) => (
                      <div key={check.key} className="flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm">
                        <span className="min-w-0 truncate">{check.label}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant={check.severity === "critical" ? "destructive" : check.severity === "ok" ? "secondary" : "outline"}>
                            {check.severity}
                          </Badge>
                          <span className="w-8 text-right font-semibold">{check.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Cash flow semana actual</p>
                  <p className="mt-1 text-lg font-semibold">{formatCents(weekOne?.netCents)}</p>
                  <p className="mt-1 text-xs text-muted-foreground">Cobros {formatCents(weekOne?.collectionsCents)} · pagos {formatCents(weekOne?.paymentsCents)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Sin fecha en forecast</p>
                  <p className="mt-1 text-lg font-semibold">{formatCents(forecastQuery.data?.forecastExclusions.missingOrInvalidDueDateAmountCents)}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{forecastQuery.data?.forecastExclusions.note || "Sin partidas excluidas por fecha"}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Cobertura</p>
                  <p className="mt-1 text-sm font-medium">{summaryQuery.data?.coverage.note}</p>
                  <p className="mt-1 text-xs text-muted-foreground">Faltan: {summaryQuery.data?.coverage.missingSources.join(", ")}</p>
                </CardContent>
              </Card>
            </div>
          </>
        )}

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {[
            "Cobrado",
            "Facturado",
            "Planificado/no facturable",
            "Cancelado",
            "Gastos",
            "Margen",
            "Saldo minimo",
            "Instalaciones con desviacion",
            "Plan vs real",
          ].map((indicator) => (
            <Card key={indicator}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-medium">{indicator}</p>
                  <Badge variant="secondary">Trazado</Badge>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Fuente real o caveat visible en esta pantalla.
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}

export function FinanceSettingsView() {
  const { user } = useAuth();
  const allowed = user ? canAccessFinanceRoute("/accounting/settings", user.role) : false;

  if (!allowed) return <AccessDenied title="Configuracion financiera" />;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <FinancePageHeader
          eyebrow="Finanzas y Contabilidad"
          eyebrowIcon={Settings2}
          title="Configuracion financiera"
          description="Parametros funcionales futuros del modulo financiero. No contiene credenciales, integraciones bancarias ni controles tecnicos."
          badgeText="Sin credenciales"
        />

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.8fr)]">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <SlidersHorizontal className="h-4 w-4" />
                Areas configurables futuras
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 sm:grid-cols-2">
              {settingsAreas.map((area) => (
                <div key={area} className="rounded-md border p-3">
                  <p className="text-sm font-medium">{area}</p>
                  <p className="mt-1 text-xs text-muted-foreground">Pendiente de contrato funcional</p>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldCheck className="h-4 w-4" />
                Limites
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
              <p>Sin credenciales tecnicas ni datos de acceso.</p>
              <Separator />
              <p>Sin cambios de permisos en esta fase.</p>
              <Separator />
              <p>Sin integracion bancaria real.</p>
              <Separator />
              <p>Sin escritura de parametros financieros todavia.</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="flex flex-col gap-3 px-4 py-4 text-sm text-muted-foreground md:flex-row md:items-center">
            <Target className="h-5 w-5 shrink-0 text-primary" />
            <span>
              La configuracion financiera debe seguir siendo funcional y auditable. La configuracion tecnica vive fuera de esta pantalla.
            </span>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
